import React, { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { ReturnIcon } from "fe-shared/src/components/Icons";
import { BusinessRole } from "fe-shared/src/types/domain";

import ParticipantInfo from "../ParticipantInfo";
import InactiveStatus from "../InactiveStatus";

import {
  getFirstAddressLine,
  getSecondAddressLine,
} from "../../helpers/address";
import {
  TransactionUser,
  TransactionWarehouse,
  TransactionType,
} from "../../types";

import styles from "./DetailsParticipantsSection.module.scss";

interface Props {
  targetWarehouse?: TransactionWarehouse;
  sourceWarehouse?: TransactionWarehouse;
  sectionAction?: React.ReactElement;
  children?: ReactNode;
  isStaffView?: boolean;
  transactionType?: TransactionType;
  submittedBy?: TransactionUser | null;
}

const DetailsParticipantsSection = ({
  sourceWarehouse,
  targetWarehouse,
  sectionAction,
  children,
  isStaffView,
  transactionType,
  submittedBy,
}: Props) => {
  const { t } = useTranslation();

  const isAdjustment = transactionType === TransactionType.Adjustment;
  const isDisposition = transactionType === TransactionType.Disposition;
  const isRetreader = transactionType === TransactionType.Retreading;
  const showTargetWarehouse = targetWarehouse && !isRetreader;

  const getParticipantActionState = (
    type: TransactionType = TransactionType.Delivery,
    isSource: boolean
  ) => {
    const shipmentPickupStates = ["shippedBy", "pickedBy"];
    const deliveryReceiptStates = ["deliveredBy", "receivedBy"];
    const isDeliveryReceiptType = [
      TransactionType.Delivery,
      TransactionType.Receipt,
    ].includes(type);
    const stateIndex = isSource ? 0 : 1;

    return isDeliveryReceiptType
      ? deliveryReceiptStates[stateIndex]
      : shipmentPickupStates[stateIndex];
  };

  const getWarehouseBlock = (
    warehouseInfo: TransactionWarehouse,
    isSourceWarehouse: boolean = false
  ) => {
    const {
      participant: { phone, businessAddress, businessRoleCode },
      signers,
      location,
      participant,
      isCompanyActive,
      isActive,
    } = warehouseInfo;
    const address = businessAddress || location?.address;
    const isWarehouseInactive =
      businessRoleCode === BusinessRole.Processor && !isActive;
    const isEmployeeInactive =
      businessRoleCode === BusinessRole.Hauler && !isActive;

    return (
      <div className={styles.warehouse}>
        <ParticipantInfo
          participant={participant}
          isStaffView={isStaffView}
          isCompanyActive={isCompanyActive}
        />
        {isWarehouseInactive && isCompanyActive && (
          <div className={styles.inactiveRow}>
            <span>{`${t("storage")} `}</span>
            <InactiveStatus />
          </div>
        )}
        <div className={styles.detailedInfo}>
          <span>{getFirstAddressLine(address)}</span>
          <span>{getSecondAddressLine(address)}</span>
          <span>{phone}</span>
        </div>
        {!isAdjustment &&
          !isDisposition &&
          signers.map((item) => {
            return (
              <div key={item.userId} className={styles.signatureBlock}>
                <div className={styles.signedBy}>
                  <div className={styles.employeeRow}>
                    <span className={styles.employeeLabel}>
                      {t(
                        `transactions.details.participantInfo.${getParticipantActionState(
                          transactionType,
                          isSourceWarehouse
                        )}`
                      )}
                    </span>
                    {isEmployeeInactive && isCompanyActive && (
                      <InactiveStatus isEmployee />
                    )}
                  </div>
                  <span className={styles.employeeName}>
                    {item.displayName ||
                      (item.userId !== null
                        ? t("transactions.details.undefinedUser")
                        : null)}
                  </span>
                </div>
                {item.signatureImageUrl && (
                  <img
                    className={styles.signaturePic}
                    src={item.signatureImageUrl}
                    alt=""
                  />
                )}
              </div>
            );
          })}
        {isSourceWarehouse && isDisposition && (
          <div className={styles.signatureBlock}>
            <div className={styles.signedBy}>
              <span className={styles.employeeLabel}>
                {t(`transactions.details.participantInfo.createdBy`)}
              </span>
              <span className={styles.employeeName}>
                {submittedBy?.displayName ||
                  (submittedBy
                    ? t("transactions.details.undefinedUser")
                    : null)}
              </span>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={styles.transactionDataWrapper}>
      <div className={styles.sectionActionContainer}>
        <div className={styles.transactionDataContainer}>
          {sourceWarehouse && (
            <>
              {getWarehouseBlock(sourceWarehouse, true)}
              {showTargetWarehouse && (
                <ReturnIcon fillColor={null} className={styles.arrow} />
              )}
            </>
          )}
          {showTargetWarehouse && getWarehouseBlock(targetWarehouse!)}
        </div>
        {sectionAction}
      </div>
      {children}
    </div>
  );
};

export default DetailsParticipantsSection;
